import { useEffect, useRef, useState } from 'react';

/**
 * A box the customer signs with a finger or stylus. Gives back a PNG (or null when cleared).
 * `initial`: a signature already given (a form picked up where it was left), shown in the box.
 */
export function SignaturePad({ onChange, label = 'Sign here', initial }: { onChange: (png: string | null) => void; label?: string; initial?: string | null }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const ink = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = Math.round(rect.width * ratio);
    c.height = Math.round(rect.height * ratio);
    const ctx = c.getContext('2d')!;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0b1220';
    if (initial) {
      const img = new Image();
      img.onload = () => {
        const r = c.getBoundingClientRect();
        ctx.drawImage(img, 0, 0, r.width, r.height);
        ink.current = true;
        setEmpty(false);
      };
      img.src = initial;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const point = (e: React.PointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const down = (e: React.PointerEvent) => {
    e.preventDefault();
    ref.current?.setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = point(e);
  };
  const move = (e: React.PointerEvent) => {
    if (!drawing.current || !last.current) return;
    const ctx = ref.current!.getContext('2d')!;
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!ink.current) {
      ink.current = true;
      setEmpty(false);
    }
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    if (ink.current) onChange(exportPng(ref.current!));
  };
  const clear = () => {
    const c = ref.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    ink.current = false;
    setEmpty(true);
    onChange(null);
  };

  return (
    <div className="sigpad">
      <canvas ref={ref} aria-label={label} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} />
      {empty ? <span className="hint-in">{label}</span> : null}
      <button type="button" className="link-btn clear" onClick={clear}>Clear</button>
    </div>
  );
}

/** A small white-background PNG of the signature (keeps the record light). */
function exportPng(c: HTMLCanvasElement): string {
  const out = document.createElement('canvas');
  const w = 600;
  const h = Math.round((c.height / c.width) * w);
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(c, 0, 0, w, h);
  return out.toDataURL('image/png');
}
